/**
 * The error notice's one-click fix chip (e.g. "Retry trusting the server certificate"; moved out of
 * `NewConnectionPopup`, C0). It flips the offending field(s) — a visible change, per the no-silent-TLS-weakening rule —
 * reruns the test, and reverts the flip unless the retry connected (`fix.keepOnFailure` opts out for the Oracle expired
 * password fix, whose server-side change has already happened). Saving still needs the normal Save.
 *
 * Round B B2 / r2: the fix OWNS each field only until someone else changes it — ownership is the field's revision, not
 * its value — and a still-owned change never outlives a failed or abandoned retry. C0: `abandon()` reverts the owned
 * fields synchronously when a draft operation voids the retry (an engine select or a patch), BEFORE the draft is
 * captured, so no engine's draft ever holds a weakened setting the user did not choose.
 */
import { useRef } from 'react';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import type { ConnectionErrorFixApply, ConnectionErrorFixRetry } from '../../utils/connectionErrorFix';
import type { ConnectionTestSnapshot, useConnectionTestOutcome } from '../../hooks/useConnectionTestOutcome';
import type { useDraftRevision } from '../../hooks/useDraftRevision';
import type { ConnectionForm } from '../state/connectionForm';
import type { ConnectionTestRun } from './useConnectionTestRun';

type FixField = keyof ConnectionErrorFixApply;
type Owned = { field: FixField; previous: unknown; setAt: number };

type Options = {
  form: ConnectionForm;
  tests: ReturnType<typeof useConnectionTestOutcome>;
  draft: ReturnType<typeof useDraftRevision>;
  runTest: () => Promise<ConnectionTestRun>;
  track: TrackFunction;
};

export function useConnectionFix({ form, tests, draft, runTest, track }: Options) {
  // Synchronous double-click guard: the async gap before the chip renders disabled would admit a second apply.
  const inFlightRef = useRef(false);
  // The fields the in-flight fix still owns (revision unchanged since it set them); empty once kept or reverted.
  const ownedRef = useRef<Owned[]>([]);

  /** Revert every field the fix still owns; returns what was reverted. */
  const revertOwned = (): Array<readonly [FixField, unknown]> => {
    const reverted: Array<readonly [FixField, unknown]> = [];
    for (const { field, previous, setAt } of ownedRef.current) {
      if (draft.fieldRevision(field) !== setAt) continue;
      form.setFieldValue(field, previous as never);
      reverted.push([field, previous]);
    }
    ownedRef.current = [];
    return reverted;
  };

  const apply = async (fix: ConnectionErrorFixRetry) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const fields = Object.entries(fix.apply) as Array<[FixField, boolean | string | undefined]>;
    const previous = fields.map(([field]) => [field, form.state.values[field]] as const);
    const fixGeneration = tests.snapshotOf().generation;
    let kept = false;
    let retry: ConnectionTestRun | undefined;
    try {
      for (const [field, value] of fields) form.setFieldValue(field, value as never);
      ownedRef.current = fix.keepOnFailure
        ? []
        : previous.map(([field, before]) => ({ field, previous: before, setAt: draft.fieldRevision(field) }));
      retry = await runTest();
      // A retry that answered for another draft generation does not keep the fix, even when it succeeded.
      kept = retry.success && !retry.discarded && tests.snapshotOf().generation === fixGeneration;
      // A validation-blocked retry never dialed and a discarded one was abandoned: neither is a fix result.
      if (!retry.discarded && !retry.blocked) {
        track('feature:connection_fix_result', { engine: form.state.values.engine, fix: fix.kind, success: retry.success, surface: 'connection-dialog' });
      }
    } finally {
      if (kept || fix.keepOnFailure) {
        ownedRef.current = [];
      } else {
        const sameGeneration = tests.snapshotOf().generation === fixGeneration;
        const reverted = revertOwned();
        // The failed retry's notice stays, re-bound to the RETRIED values with only the reverted fields restored.
        if (retry?.snapshot && retry.values && sameGeneration) {
          const rebound: ConnectionTestSnapshot = tests.snapshotOf({ ...retry.values, ...Object.fromEntries(reverted) });
          tests.rebase(retry.snapshot, rebound);
        }
      }
      inFlightRef.current = false;
    }
  };

  /** A draft operation voided the retry: its still-owned change goes now, before any draft captures it. */
  const abandon = () => {
    revertOwned();
  };

  return { apply, abandon };
}
